import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import { excludeDeleted, sortBySequenceNumber } from 'shared/lib/common-util'
import { Document, DocumentFile } from '../models/types'
import { ErrorResult } from '../../../../shared/models/types'
import { BadRequestResponse } from '../../../../types/Errors'
import { collectDeletableDocumentFiles, collectSavedDocumentFilesToBeUploaded, isDocumentEqual } from '../models/selectors'
import { DocumentCreate, DocumentUpdate, DocumentSaveBatch } from '../models/request'
import { fromDocumentFileSaves } from '../models/modelToModel'
import { toFormData } from '../models/formValuesToModel'
import { deleteFile } from '../api/deleteFile'
import { postFile } from '../api/postFile'

type CreateDocumentsFn = (documents: DocumentCreate[]) => Promise<Document[]>
type UpdateDocumentsFn = (documents: DocumentUpdate[]) => Promise<Document[]>

export type DocumentSaveResult = {
  data: Document[]
  documentErrors: ErrorResult[]
  errors: ErrorResult[]
}

function collectSaveErrors(rejectedResult: PromiseRejectedResult): ErrorResult[] {
  const errorResponseData = rejectedResult?.reason?.response?.data

  if (errorResponseData?.message === 'CONSTRAINT_VIOLATION') {
    const constraintErrors = (errorResponseData.errors ?? []).map((errorItem: BadRequestResponse) => ({
      message: errorResponseData.message,
      field: errorItem.field,
      code: errorItem.reason,
    }))

    if (constraintErrors.length) return constraintErrors
  }

  const reason = rejectedResult?.reason
  return [{
    message: errorResponseData?.message ?? (reason instanceof Error ? reason.message : 'Failed to save some documents'),
    code: reason?.response?.status != null ? String(reason.response.status) : undefined,
  }]
}

async function createOrUpdateDocuments(
  batch: DocumentSaveBatch,
  createDocuments: CreateDocumentsFn,
  updateDocuments: UpdateDocumentsFn,
) {
  const documentRequests = []
  if (batch.creates.length) documentRequests.push(createDocuments(batch.creates))
  if (batch.updates.length) documentRequests.push(updateDocuments(batch.updates))
  const results = await Promise.allSettled(documentRequests)

  const errors = results
    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    .flatMap((result) => collectSaveErrors(result))

  const data = results
    .filter((result): result is PromiseFulfilledResult<Document[]> => result.status === 'fulfilled')
    .flatMap((result) => result.value)

  return { data, errors }
}

async function uploadFiles(
  files: FileType[],
) {
  if (!files.length) return { saved: [], errors: [] }

  const fileRequests = files.map((
    file,
  ) => postFile(file.documentId as number, toFormData(file)))

  const fileResults = await Promise.allSettled(fileRequests)
  const errors: ErrorResult[] = fileResults.some((result) => result.status === 'rejected')
    ? [{ message: 'Failed to upload some files' }]
    : []

  const saved = fileResults
    .filter((result): result is PromiseFulfilledResult<DocumentFile> => result.status === 'fulfilled')
    .map((result) => result.value)

  return { saved, errors }
}

async function deleteFiles(
  files: FileType[],
): Promise<{ errors: ErrorResult[] }> {
  const deleteRequests = files.flatMap((f) => {
    if (!f.uuid) return []
    const [folder, fileUuid] = f.uuid.split('/')

    return [deleteFile({ folder, fileUuid })]
  })

  if (!deleteRequests.length) return { errors: [] }

  const deleteFilePromises = await Promise.allSettled(deleteRequests)
  const hasFailures = deleteFilePromises.some((result): result is PromiseRejectedResult => result.status === 'rejected')
  const errors: ErrorResult[] = hasFailures ? [{ message: 'Failed to delete some files' }] : []

  return { errors }
}

function mergeSavedDocumentsWithFiles(
  allDocuments: DocumentCreate[],
  savedDocuments: Document[],
  uploadedFiles: DocumentFile[],
) {
  const uploadedFilesByDocumentId = new Map<number, DocumentFile[]>()

  uploadedFiles.forEach((file) => {
    if (!file.documentId) return
    const existing = uploadedFilesByDocumentId.get(file.documentId) ?? []
    existing.push(file)
    uploadedFilesByDocumentId.set(file.documentId, existing)
  })

  for (const savedDocument of savedDocuments) {
    if (!savedDocument.id) continue

    const existingDocument = allDocuments.find((d) => isDocumentEqual(d, savedDocument))
    if (!existingDocument) continue

    const existingFiles = fromDocumentFileSaves(existingDocument.files.filter(excludeDeleted))

    const mergedFiles = [...existingFiles]

    const newUploadedFiles = uploadedFilesByDocumentId.get(savedDocument.id) ?? []

    for (const uploadedFile of newUploadedFiles) {
      const idx = mergedFiles.findIndex((f: DocumentFile) => f.id === uploadedFile.id)
      if (idx >= 0) {
        mergedFiles[idx] = uploadedFile
      } else {
        mergedFiles.push(uploadedFile)
      }
    }

    savedDocument.files = mergedFiles.sort(sortBySequenceNumber)
  }

  return savedDocuments.sort(sortBySequenceNumber)
}

function createDocumentSaver({
  createDocumentsFn,
  updateDocumentsFn,
}: {
  createDocumentsFn: CreateDocumentsFn
  updateDocumentsFn: UpdateDocumentsFn
}) {
  return async function saveDocuments(batch: DocumentSaveBatch) {
    const allDocuments = [...batch.creates, ...batch.updates]
    const {
      data: savedDocuments = [],
      errors: saveDocumentsErrors,
    } = await createOrUpdateDocuments(batch, createDocumentsFn, updateDocumentsFn)

    const {
      saved: savedFiles,
      errors: uploadFilesErrors,
    } = await uploadFiles(collectSavedDocumentFilesToBeUploaded(savedDocuments, allDocuments))

    const { errors: deleteFilesErrors } = await deleteFiles(collectDeletableDocumentFiles(allDocuments))

    return {
      data: mergeSavedDocumentsWithFiles(allDocuments, savedDocuments, savedFiles),
      documentErrors: saveDocumentsErrors,
      errors: [
        ...saveDocumentsErrors,
        ...uploadFilesErrors,
        ...deleteFilesErrors,
      ],
    }
  }
}

export default createDocumentSaver

